import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Container, Stack } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserShield } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

import { getRuta } from "../../../helpers/Helpers";
import { getToken } from "../../../api/Account";
import { addRolAsync, getRolListAsync } from "../../../api/Rols";
import { EstadoError, PageHeader, useAviso } from "../../../components/base";
import {
  CargandoFormulario,
  llevarAlPrimerError,
  useCargaLocal,
  useConfirmacion,
  useEnvio,
  useEscritura,
} from "../comunes";
import FormularioRol, { validarRol } from "./FormularioRol";
import { filasParaServidor, permisosDesdeFilas } from "./permisos";
import { pedirTodo } from "./confirmaciones";

const DATOS_VACIOS = { rolName: "", idRolSuperior: "" };

const AddRol = () => {
  const token = getToken();
  let ruta = getRuta();
  let navigate = useNavigate();
  const aviso = useAviso();
  const escribir = useEscritura();
  const { guardando, enviar } = useEnvio();
  const { pedir: pedirConfirmacion, dialogo: confirmacion } = useConfirmacion();
  const formulario = useRef(null);

  const [roles, setRoles] = useState([]);
  const [datos, setDatos] = useState(DATOS_VACIOS);
  const [permisos, setPermisos] = useState(() => permisosDesdeFilas([]));

  const [intentado, setIntentado] = useState(false);
  const errores = intentado ? validarRol(datos, roles) : {};

  const { cargando, error, cargar } = useCargaLocal(() => getRolListAsync(token));

  const getRoles = async () => {
    const result = await cargar();
    if (result) setRoles(result.data.result || []);
  };

  useEffect(() => {
    getRoles();
  }, []);

  const onCampo = (campo, valor) => setDatos((actual) => ({ ...actual, [campo]: valor }));

  const saveRol = () =>
    enviar(async () => {
      setIntentado(true);
      if (Object.keys(validarRol(datos, roles)).length > 0) {
        llevarAlPrimerError(formulario);
        return;
      }
      // "Acceso total" no da permisos (solo cuentan las casillas): se manda apagado para que el
      // servidor guarde exactamente lo marcado. Antes, con el interruptor encendido, guardaba todo
      // en true aunque se hubieran desmarcado casillas.
      const ok = await escribir(
        () =>
          addRolAsync(token, {
            roleName: datos.rolName.trim(),
            isFullAccess: false,
            nivelJerarquico: datos.idRolSuperior,
            permissions: filasParaServidor(permisos),
          }),
        "No se pudo agregar el rol"
      );
      if (!ok) return;
      const nombre = datos.rolName.trim();
      setDatos(DATOS_VACIOS);
      setPermisos(permisosDesdeFilas([]));
      setIntentado(false);
      getRoles();
      aviso.exito(`Rol ${nombre} agregado`);
    });

  return (
    <Container maxWidth={false} disableGutters>
      <PageHeader
        titulo="Nuevo rol"
        subtitulo="Define qué pueden ver y hacer sus usuarios"
        icono={<FontAwesomeIcon icon={faUserShield} />}
        acciones={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`${ruta}/rols`)}
          >
            Regresar
          </Button>
        }
      />

      {error ? (
        <EstadoError
          titulo={
            error === "conexion"
              ? "No se pudo conectar con el servidor"
              : "No se pudo cargar el formulario"
          }
          mensaje="Faltan los roles para elegir el rol superior. Revisa tu conexión e inténtalo de nuevo."
          onReintentar={getRoles}
        />
      ) : cargando ? (
        <CargandoFormulario etiqueta="Cargando el formulario…" />
      ) : (
        <Stack spacing={3} ref={formulario}>
          <FormularioRol
            datos={datos}
            onCampo={onCampo}
            permisos={permisos}
            onPermisos={setPermisos}
            onTodo={(valor) => pedirTodo(pedirConfirmacion, valor, setPermisos)}
            roles={roles}
            errores={errores}
            deshabilitado={guardando}
          />

          <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" } }}>
            <Button
              variant="contained"
              size="large"
              disabled={guardando}
              onClick={() => saveRol()}
              startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ width: { xs: "100%", sm: "auto" }, minWidth: 220 }}
            >
              {guardando ? "Guardando…" : "Agregar rol"}
            </Button>
          </Box>
        </Stack>
      )}

      {confirmacion}
    </Container>
  );
};

export default AddRol;
